// Parses the free-text `opening_hours` kiosk SSM param, e.g.:
//   "(Europe/Copenhagen) Mo-Fr 07:00-20:00; Sa-Su 09:00-18:00"
//   "(Europe/Copenhagen) 24/7"
//   "closed"
//
// These values follow the OSM opening_hours convention
// (https://wiki.openstreetmap.org/wiki/Key:opening_hours), prefixed with an
// IANA timezone in parens. This only implements the subset actually used in
// real kiosk data (day-ranges, time-ranges, semicolon-separated rules,
// "24/7", "closed") — not the full spec (public holidays, exceptions,
// sunrise/sunset, comments, etc.). If broader compatibility is ever needed,
// consider swapping this for the official `opening_hours.js` package rather
// than extending this parser piecemeal. Anything this doesn't cleanly match
// returns null — callers must treat that as "can't evaluate", never guess.

interface OpeningHoursRule {
  days: number[]; // 0=Sun .. 6=Sat
  startMinutes: number;
  endMinutes: number; // exclusive; 1440 represents "24:00"
}

export type OpeningHoursSpec =
  | { type: "always-closed" }
  | { type: "always-open" }
  | { type: "scheduled"; timeZone: string; rules: OpeningHoursRule[] };

const DAY_INDEX: Record<string, number> = { su: 0, mo: 1, tu: 2, we: 3, th: 4, fr: 5, sa: 6 };
const WEEKDAY_FROM_INTL: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function parseTime(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (minute > 59) return null;
  if (hour === 24 && minute === 0) return 24 * 60;
  if (hour > 23) return null;
  return hour * 60 + minute;
}

// Inclusive day range that wraps around the week — handles both ordinary
// ranges (Mo-Fr) and "backwards" ones (Su-Sa, Mo-Su) that mean every day.
function daysInRange(start: number, end: number): number[] {
  const days: number[] = [];
  let i = start;
  while (true) {
    days.push(i);
    if (i === end) break;
    i = (i + 1) % 7;
  }
  return days;
}

function parseRule(rule: string): OpeningHoursRule | null {
  const match = /^(?:([a-zA-Z]{2})(?:-([a-zA-Z]{2}))?\s+)?(\d{2}:\d{2})-(\d{2}:\d{2})$/.exec(rule.trim());
  if (!match) return null;
  const [, dayStart, dayEnd, startStr, endStr] = match;

  const startMinutes = parseTime(startStr);
  const endMinutes = parseTime(endStr);
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return null;

  let days: number[];
  if (dayStart) {
    const startIdx = DAY_INDEX[dayStart.toLowerCase()];
    const endIdx = dayEnd ? DAY_INDEX[dayEnd.toLowerCase()] : startIdx;
    if (startIdx === undefined || endIdx === undefined) return null;
    days = daysInRange(startIdx, endIdx);
  } else {
    days = [0, 1, 2, 3, 4, 5, 6];
  }

  return { days, startMinutes, endMinutes };
}

export function parseOpeningHours(value: string): OpeningHoursSpec | null {
  const trimmed = value.trim();
  if (trimmed.toLowerCase() === "closed") return { type: "always-closed" };

  const match = /^\(([^)]+)\)\s*(.+)$/.exec(trimmed);
  if (!match) return null;
  const [, timeZone, rest] = match;

  if (rest.trim() === "24/7") return { type: "always-open" };

  const rules = rest.split(";").map((r) => parseRule(r));
  if (rules.some((r) => r === null)) return null;

  return { type: "scheduled", timeZone, rules: rules as OpeningHoursRule[] };
}

function zonedWeekdayAndMinutes(date: Date, timeZone: string): { weekday: number; minutes: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const weekdayStr = parts.find((p) => p.type === "weekday")?.value;
    const hourStr = parts.find((p) => p.type === "hour")?.value;
    const minuteStr = parts.find((p) => p.type === "minute")?.value;
    if (!weekdayStr || hourStr === undefined || minuteStr === undefined) return null;

    const weekday = WEEKDAY_FROM_INTL[weekdayStr];
    if (weekday === undefined) return null;

    // Some locales render midnight as "24" with hour12:false.
    const hour = Number(hourStr) % 24;
    return { weekday, minutes: hour * 60 + Number(minuteStr) };
  } catch {
    return null; // unknown/invalid IANA timezone
  }
}

/** Returns null when it can't be evaluated (unknown timezone) — never guess. */
export function isWithinOpeningHours(spec: OpeningHoursSpec, now: Date): boolean | null {
  if (spec.type === "always-closed") return false;
  if (spec.type === "always-open") return true;

  const zoned = zonedWeekdayAndMinutes(now, spec.timeZone);
  if (!zoned) return null;

  return spec.rules.some(
    (rule) => rule.days.includes(zoned.weekday) && zoned.minutes >= rule.startMinutes && zoned.minutes < rule.endMinutes,
  );
}
